import {
	FinancingActionItemCode,
	FulfillmentActionItemCode,
	PaymentActionItemCode,
	SecurityActionItemCode,
	TarActionItemCode,
	TempTagIssuanceWorkflow,
} from '@ekhodealer/ekho-common';
import { Stack, Text } from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

import { ApproveRobosignDetail } from './action-item-details/ApproveRobosignDetail';
import { AssignMotorcycleEngineNumberDetail } from './action-item-details/AssignMotorcycleEngineNumberDetail';
import { AssignOdometerReadingDetail } from './action-item-details/AssignOdometerReadingDetail';
import { AssignVinDetail } from './action-item-details/AssignVinDetail';
import { CompleteMSODetail } from './action-item-details/CompleteMSODetail';
import { CompleteWarrantyRegistrationDetail } from './action-item-details/CompleteWarrantyRegistrationDetail';
import { FinalizeDealInInventorySystemDetail } from './action-item-details/FinalizeDealInInventorySystemDetail';
import { GeneratePlateDetail } from './action-item-details/GeneratePlateDetail';
import { GenerateTempTagEkhoDetail } from './action-item-details/GenerateTempTagEkhoDetail';
import { GenerateTempTagExternalDetail } from './action-item-details/GenerateTempTagExternalDetail';
import { MarkAssembledDetail } from './action-item-details/MarkAssembledDetail';
import { MarkBackfillAccessoriesRequestedDetail } from './action-item-details/MarkBackfillAccessoriesRequestedDetail';
import { MarkBackfillInventoryAddedToInventorySystemDetail } from './action-item-details/MarkBackfillInventoryAddedToInventorySystemDetail';
import { MarkDeliveryFulfilledDetail } from './action-item-details/MarkDeliveryFulfilledDetail';
import { MarkInspectedDetail } from './action-item-details/MarkInspectedDetail';
import { MarkInTransitToBuyerDetail } from './action-item-details/MarkInTransitToBuyerDetail';
import { MarkInTransitToFulfillmentCenterDetail } from './action-item-details/MarkInTransitToFulfillmentCenterDetail';
import { MarkPickupFulfilledDetail } from './action-item-details/MarkPickupFulfilledDetail';
import { MarkPickupScheduledDetail } from './action-item-details/MarkPickupScheduledDetail';
import { MarkProductionCompleteDetail } from './action-item-details/MarkProductionCompleteDetail';
import { MarkReceivedAtFulfillmentCenterDetail } from './action-item-details/MarkReceivedAtFulfillmentCenterDetail';
import { ScanAndUploadFormsDetail } from './action-item-details/ScanAndUploadFormsDetail';
import { ScheduleCounterSignRobosignSessionDetail } from './action-item-details/ScheduleCounterSignRobosignSessionDetail';
import { SellerRonSessionDetail } from './action-item-details/SellerRonSessionDetail';
import { ShipBuyerPacketToSellerDetail } from './action-item-details/ShipBuyerPacketToSellerDetail';
import { ShipSellerFormsToEkhoDetail } from './action-item-details/ShipSellerFormsToEkhoDetail';
import { SignDocumentDetail } from './action-item-details/SignDocumentDetail';
import { SubmitSignedTarPaperworkDetail } from './action-item-details/SubmitSignedTarPaperworkDetail';

interface ActionItemDetailViewProps {
	/** The action item to display in detail */
	actionItem: ActionItem;
}

// ============================================================================
// Placeholder Detail Components
// These will be moved to separate files as they are implemented
// ============================================================================

interface ActionItemDetailProps {
	actionItem: ActionItem;
}

const PayInFullDetail = ({ actionItem }: ActionItemDetailProps) => (
	<Stack itemsSpacing='16'>
		<Text size='body' color='secondary'>
			Pay In Full detail view for: {actionItem.config.title}
		</Text>
	</Stack>
);

const ReviewRiskAssessmentDetail = ({ actionItem }: ActionItemDetailProps) => (
	<Stack itemsSpacing='16'>
		<Text size='body' color='secondary'>
			Review Risk Assessment detail view for: {actionItem.config.title}
		</Text>
	</Stack>
);

const DefaultActionItemDetail = ({ actionItem }: ActionItemDetailProps) => (
	<Stack itemsSpacing='16'>
		<Text size='body' color='secondary'>
			No detail view available for action code: {actionItem.actionCode}
		</Text>
	</Stack>
);

/**
 * ActionItemDetailView component that renders the appropriate detail view
 * based on the action item's action code.
 */
export const ActionItemDetailView = ({ actionItem }: ActionItemDetailViewProps) => {
	const renderDetailContent = () => {
		switch (actionItem.actionCode) {
			// Payment action items
			case PaymentActionItemCode.PAY_IN_FULL:
				return <PayInFullDetail actionItem={actionItem} />;
			case PaymentActionItemCode.COUNTER_SIGN_PURCHASE_AGREEMENT:
			case PaymentActionItemCode.COUNTER_SIGN_SALE_DOCUMENT:
				return <SignDocumentDetail actionItem={actionItem} />;

			// Security action items
			case SecurityActionItemCode.REVIEW_RISK_ASSESSMENT:
				return <ReviewRiskAssessmentDetail actionItem={actionItem} />;

			// Financing action items
			case FinancingActionItemCode.SIGN_LOAN_CONTRACT:
			case FinancingActionItemCode.COUNTER_SIGN_LOAN_CONTRACT:
				return <SignDocumentDetail actionItem={actionItem} />;

			// Fulfillment action items
			case FulfillmentActionItemCode.MARK_DELIVERY_FULFILLED:
				return <MarkDeliveryFulfilledDetail actionItem={actionItem} />;
			case FulfillmentActionItemCode.MARK_ASSEMBLED:
				return <MarkAssembledDetail actionItem={actionItem} />;
			case FulfillmentActionItemCode.MARK_IN_TRANSIT_TO_FULFILLMENT_CENTER:
				return <MarkInTransitToFulfillmentCenterDetail actionItem={actionItem} />;
			case FulfillmentActionItemCode.MARK_RECEIVED_AT_FULFILLMENT_CENTER:
				return <MarkReceivedAtFulfillmentCenterDetail actionItem={actionItem} />;
			case FulfillmentActionItemCode.MARK_IN_TRANSIT_TO_BUYER:
				return <MarkInTransitToBuyerDetail actionItem={actionItem} />;
			case FulfillmentActionItemCode.MARK_INSPECTED:
				return <MarkInspectedDetail actionItem={actionItem} />;
			case FulfillmentActionItemCode.MARK_PICKUP_SCHEDULED:
				return <MarkPickupScheduledDetail actionItem={actionItem} />;
			case FulfillmentActionItemCode.MARK_PICKUP_FULFILLED:
				return <MarkPickupFulfilledDetail actionItem={actionItem} />;
			case FulfillmentActionItemCode.MARK_BACKFILL_INVENTORY_ADDED_TO_INVENTORY_SYSTEM:
				return (
					<MarkBackfillInventoryAddedToInventorySystemDetail actionItem={actionItem} />
				);
			case FulfillmentActionItemCode.MARK_BACKFILL_ACCESSORIES_REQUESTED:
				return <MarkBackfillAccessoriesRequestedDetail actionItem={actionItem} />;

			// TAR action items
			case TarActionItemCode.ASSIGN_VIN:
				return <AssignVinDetail actionItem={actionItem} />;
			case TarActionItemCode.ASSIGN_ODOMETER_READING:
				return <AssignOdometerReadingDetail actionItem={actionItem} />;
			case TarActionItemCode.ASSIGN_MOTORCYCLE_ENGINE_NUMBER:
				return <AssignMotorcycleEngineNumberDetail actionItem={actionItem} />;
			case TarActionItemCode.MARK_PRODUCTION_COMPLETE:
				return <MarkProductionCompleteDetail actionItem={actionItem} />;
			case TarActionItemCode.COUNTER_SIGN_TAR_PAPERWORK:
				return <SignDocumentDetail actionItem={actionItem} />;
			case TarActionItemCode.GENERATE_TEMP_TAG: {
				const issuanceWorkflow = actionItem.metadata?.issuanceWorkflow as
					| TempTagIssuanceWorkflow
					| undefined;

				switch (issuanceWorkflow) {
					case TempTagIssuanceWorkflow.EKHO_TEMP_TAG_PACKET:
						return <GenerateTempTagEkhoDetail actionItem={actionItem} />;
					case TempTagIssuanceWorkflow.BOOKLET:
					case TempTagIssuanceWorkflow.EXTERNAL_PORTAL:
					case TempTagIssuanceWorkflow.INTERNAL_PORTAL:
						return <GenerateTempTagExternalDetail actionItem={actionItem} />;
					default:
						return <GenerateTempTagExternalDetail actionItem={actionItem} />;
				}
			}
			case TarActionItemCode.GENERATE_PLATE:
				return <GeneratePlateDetail actionItem={actionItem} />;
			case TarActionItemCode.COMPLETE_MSO:
				return <CompleteMSODetail actionItem={actionItem} />;
			case TarActionItemCode.SCAN_AND_UPLOAD_FORMS:
				return <ScanAndUploadFormsDetail actionItem={actionItem} />;
			case TarActionItemCode.SUBMIT_SIGNED_TAR_PAPERWORK:
				return <SubmitSignedTarPaperworkDetail actionItem={actionItem} />;
			case TarActionItemCode.COMPLETE_WARRANTY_REGISTRATION:
				return <CompleteWarrantyRegistrationDetail actionItem={actionItem} />;
			case TarActionItemCode.FINALIZE_DEAL_IN_INVENTORY_SYSTEM:
				return <FinalizeDealInInventorySystemDetail actionItem={actionItem} />;
			case TarActionItemCode.SHIP_BUYER_PACKET_TO_SELLER:
				return <ShipBuyerPacketToSellerDetail actionItem={actionItem} />;
			case TarActionItemCode.SHIP_SELLER_FORMS_TO_EKHO:
				return <ShipSellerFormsToEkhoDetail actionItem={actionItem} />;

			// RON action items
			case TarActionItemCode.SELLER_RON_SESSION:
				return <SellerRonSessionDetail actionItem={actionItem} />;

			// Robotic signing action items
			case TarActionItemCode.SCHEDULE_COUNTER_SIGN_ROBOSIGN_SESSION:
				return <ScheduleCounterSignRobosignSessionDetail actionItem={actionItem} />;
			case TarActionItemCode.APPROVE_ROBOSIGN_TAR_PAPERWORK:
			case TarActionItemCode.APPROVE_COUNTER_SIGN_ROBOSIGN_TAR_PAPERWORK:
				return <ApproveRobosignDetail actionItem={actionItem} />;

			default:
				return <DefaultActionItemDetail actionItem={actionItem} />;
		}
	};

	return <Stack itemsSpacing='24'>{renderDetailContent()}</Stack>;
};
